import React, { useEffect } from 'react';
import Table, { CellProps } from 'rsuite/Table';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { ModalsService } from '../../../services/modals.service';
import { usePaginationQs } from '../../../utils/usePagination';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';
import { ToggleCell } from '../../common/table/toggle-cell';
import { AlertService } from '../../../services/alert.service';
import { AddBanner, BannerItem } from './AddBanner';
import { ImagesCell } from '../../common/cards/images-cell-card.component';
import { trpcSwr } from '../../../api/trpc';
import moment from 'moment';
import { Item } from './OtcRecommendationTab';

export const BannersTab = () => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(20);

  const { data, error, isLoading, mutate } =
    trpcSwr.otcBannerAdmin.list.useSWR<{
      items: BannerItem[];
      total: number;
    }>(
      {
        take,
        skip,
      },
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      },
    );

  const { trigger } = trpcSwr.otcBannerAdmin.update.useSWRMutation();
  async function toggleDisabled(rowData: BannerItem, value: boolean) {
    await trigger({ id: rowData.id, data: { disabled: value } });
    await mutate();

    AlertService.success();
  }

  const renderMenu =
    (banner: BannerItem): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 'edit') {
          ModalsService.createModal(AddBanner, { banner }).then(() => mutate());
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'edit'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const DataFinishedAtCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<BannerItem>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {moment(rowData.finishedAt).format('DD.MM.YYYY')}
        </Table.Cell>
      )
    );
  };

  const DataUpdatedAtCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<BannerItem>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {moment(rowData.updatedAt).format('DD.MM.YYYY')}
        </Table.Cell>
      )
    );
  };

  const DataCreatedAtCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<BannerItem>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {moment(rowData.createdAt).format('DD.MM.YYYY')}
        </Table.Cell>
      )
    );
  };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<BannerItem>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  if ((error || !data) && !isLoading) {
    return <div className="pb-4">Неизвестная ошибка</div>;
  }

  return (
    <div>
      {isLoading ? (
        <MyLoader />
      ) : (
        <>
          {' '}
          <div>Баннеры отображаются при количестве активных 3 и более</div>
          <Table
            loading={isLoading}
            data={data?.items}
            headerHeight={57}
            autoHeight
            rowKey="id"
            locale={{
              loading: 'Загрузка',
              emptyMessage: 'Нет баннеров',
            }}
          >
            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Внутреннее название</Table.HeaderCell>
              <Table.Cell dataKey="name" />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Изображение</Table.HeaderCell>
              <Table.Cell>
                {({ image }: BannerItem) =>
                  image ? <ImagesCell imagesList={[image]} /> : null
                }
              </Table.Cell>
            </Table.Column>

            <Table.Column fullText width={100}>
              <Table.HeaderCell>Показы</Table.HeaderCell>
              <Table.Cell>
                {({ views }: Item) => (views ? `${views}` : null)}
              </Table.Cell>
            </Table.Column>

            <Table.Column fullText width={100}>
              <Table.HeaderCell>Клики</Table.HeaderCell>
              <Table.Cell>
                {({ clicks }: Item) => (clicks ? `${clicks}` : null)}
              </Table.Cell>
            </Table.Column>

            <Table.Column fullText>
              <Table.HeaderCell>CTR%</Table.HeaderCell>
              <Table.Cell>
                {({ ctr }: Item) => (ctr ? (ctr * 100).toFixed(6) : null)}
              </Table.Cell>
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Приоритет</Table.HeaderCell>
              <Table.Cell dataKey="priority" />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Показывать до</Table.HeaderCell>
              <DataFinishedAtCell />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Последнее обновление</Table.HeaderCell>
              <DataUpdatedAtCell />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Дата создания</Table.HeaderCell>
              <DataCreatedAtCell />
            </Table.Column>

            <Table.Column>
              <Table.HeaderCell>Включен</Table.HeaderCell>
              <Table.Cell>
                {(rowData: BannerItem) => {
                  const now = moment.utc().startOf('day');
                  const isExpired = moment(rowData.finishedAt).isBefore(
                    now,
                    'day',
                  );
                  return (
                    <ToggleCell
                      rowData={rowData}
                      dataKey="disabled"
                      inverted={true}
                      onChange={toggleDisabled}
                      disabled={isExpired}
                      renderAsCell={false}
                    />
                  );
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column>
              <Table.HeaderCell> </Table.HeaderCell>
              <ActionCell />
            </Table.Column>
          </Table>
          {data && data.total > take && (
            <Pagination
              className="mt-2 pb-4"
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="md"
              layout={['pager']}
              total={data.total}
              limit={take}
              activePage={page}
              onChangePage={handleSetPage}
              onChangeLimit={handleChangeLimit}
            />
          )}
        </>
      )}
    </div>
  );
};
